/**
 * Daily Briefing Workflow
 *
 * This workflow generates a daily briefing for users:
 * 1. Scheduled to run every morning
 * 2. Gathers today's events and meetings
 * 3. Lists tasks due today or overdue
 * 4. Generates AI summary and sends notification
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const dailyBriefingWorkflow: Workflow = {
  id: 'daily-briefing',
  name: 'Briefing Giornaliero',
  description: 'Genera automaticamente ogni mattina un riepilogo della giornata con eventi in calendario, task in scadenza e priorità del giorno',
  trigger: {
    type: 'schedule',
    config: {
      // Run every day at 7:00 AM
      cron: '0 7 * * *',
      timezone: 'Europe/Rome',
      // Don't run on weekends (optional)
      excludeDays: ['saturday', 'sunday'],
    },
  },
  steps: [
    {
      id: 'gather_events',
      name: 'Raccogli Eventi',
      type: 'custom',
      config: {
        // Fetch today's calendar events
        action: 'fetch_calendar_events',
        dateRange: {
          start: 'today',
          end: 'today',
        },
        includeTypes: ['meeting', 'appointment', 'videocall', 'event'],
        sortBy: 'startTime',
      },
      next: ['gather_tasks'],
    },
    {
      id: 'gather_tasks',
      name: 'Raccogli Task',
      type: 'custom',
      config: {
        // Fetch tasks due today or overdue
        action: 'fetch_tasks',
        filters: {
          // Tasks due today or already overdue
          dueDate: {
            lte: 'today',
          },
          status: {
            neq: 'completed',
          },
        },
        sortBy: ['priority', 'dueDate'],
        limit: 20,
      },
      next: ['generate_briefing'],
    },
    {
      id: 'generate_briefing',
      name: 'Genera Briefing',
      type: 'ai_summarize',
      config: {
        prompt: `Genera un briefing giornaliero conciso e motivante.

Includi:
1. Saluto personalizzato
2. Riepilogo eventi del giorno (orari e titoli)
3. Task in scadenza oggi (evidenzia priorità alte)
4. Task scaduti che richiedono attenzione
5. Suggerimento di focus per la giornata

Stile:
- Professionale ma amichevole
- Massimo 150 parole
- Usa bullet points
- Evidenzia le priorità

Dati disponibili:
- Eventi: {events}
- Task: {tasks}`,
        includeUserContext: true,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Invia Briefing',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: '{briefingSummary}',
        title: 'Buongiorno! Ecco il tuo briefing di oggi',
        priority: 'normal',
        action: {
          label: 'Vedi Calendario',
          handler: 'open_calendar',
        },
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format events for briefing display
 */
export function formatEventsForBriefing(
  events: Array<{ title: string; startTime: string; endTime: string }>
): string {
  if (events.length === 0) return 'Nessun evento in calendario oggi.';

  const formatted = events.map(event => {
    const start = new Date(event.startTime).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `• ${start} - ${event.title}`;
  });

  return formatted.join('\n');
}

/**
 * Format tasks for briefing display
 */
export function formatTasksForBriefing(
  tasks: Array<{ title: string; priority?: string; dueDate: string }>
): string {
  if (tasks.length === 0) return 'Nessun task in scadenza oggi.';

  const formatted = tasks.map(task => {
    const priorityEmoji =
      task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢';
    return `${priorityEmoji} ${task.title}`;
  });

  return formatted.join('\n');
}

/**
 * Check if date is today
 */
export function isToday(date: string | Date): boolean {
  const today = new Date();
  const checkDate = new Date(date);

  return (
    checkDate.getDate() === today.getDate() &&
    checkDate.getMonth() === today.getMonth() &&
    checkDate.getFullYear() === today.getFullYear()
  );
}
